import { push } from 'redux-first-history';
import { Api } from 'infrastructure/api/Api';
import { ApiPayload } from 'infrastructure/api/ApiTypes';
import { LoginUser } from 'models/LoginUser';
import { User } from 'models/User';
import { ProfileSettings } from 'models/ProfileSettings';
import { RegisterUser } from 'models/ViewModels/RegisterUser';

export const LoginUserActionType = {
    REGISTER_USER: "REGISTER_USER",
    REGISTER_USER_SUCCESS: "REGISTER_USER_SUCCESS",
    LOGIN_USER: "LOGIN_USER",
    LOGIN_USER_SUCCESS: "LOGIN_USER_SUCCESS",
    CHANGE_PASSWORD: "CHANGE_PASSWORD",
    CHANGE_PASSWORD_SUCCESS: "CHANGE_PASSWORD_SUCCESS",
    SETNEW_PASSWORD: "SETNEW_PASSWORD",
    SETNEW_PASSWORD_SUCCESS: "SETNEW_PASSWORD_SUCCESS",
    RESET_PASSWORD: "RESET_PASSWORD",
    RESET_PASSWORD_SUCCESS: "RESET_PASSWORD_SUCCESS",
    ADD_ROLE_LOGIN_USER: "ADD_ROLE_LOGIN_USER",
    ADD_ROLE_LOGIN_USER_SUCCESS: "ADD_ROLE_LOGIN_USER_SUCCESS",
    REMOVE_ROLE_LOGIN_USER: "REMOVE_ROLE_LOGIN_USER",
    REMOVE_ROLE_LOGIN_USER_SUCCESS: "REMOVE_ROLE_LOGIN_USER_SUCCESS",
    FETCH_USERDATA: 'FETCH_USERDATA',
    FETCH_USERDATA_SUCCESS: 'FETCH_USERDATA_SUCCESS',
    FETCH_SETTINGS: 'FETCH_SETTINGS',
    FETCH_SETTINGS_SUCCESS: 'FETCH_SETTINGS_SUCCESS',
    UPDATE_SETTINGS: 'UPDATE_SETTINGS',
    UPDATE_SETTINGS_SUCCESS: 'UPDATE_SETTINGS_SUCCESS',
    LOGOFF_USER: "LOGOFF_USER",
    LOGIN_USER_SUCCESS_REDIRECT: "LOGIN_USER_SUCCESS_REDIRECT",
    LOGIN_ERROR: "LOGIN_ERROR",
    API_ERROR: 'API_ERROR',
    DELETE_ACCOUNT: 'DELETE_ACCOUNT',
    DELETE_ACCOUNT_SUCCESS: 'DELETE_ACCOUNT_SUCCESS',
    CONFIRM_DELETE_ACCOUNT: 'CONFIRM_DELETE_ACCOUNT',
    CONFIRM_DELETE_ACCOUNT_SUCCESS: 'CONFIRM_DELETE_ACCOUNT_SUCCESS',
};

export interface LoginData {
    userName: string,
    password: string
}

export interface ChangePasswordData {
    eMail: string,
    newPassword: string,
    confirmNewPassword: string,
    oldPassword: string
}

export interface ChangeRoleData {
    userId: string,
    roleId: string
}

interface ILoginUserAction {
    type: typeof LoginUserActionType.LOGIN_USER,
    payload: ApiPayload,
    meta: {
        isApiCall: boolean
    },
    token: string
}

interface ILogOffUserAction {
    type: typeof LoginUserActionType.LOGOFF_USER,
    token: string
}

interface IChangePassword {
    type: typeof LoginUserActionType.CHANGE_PASSWORD,
    payload: ApiPayload,
    meta: {
        isApiCall: boolean
    },
    token: string
}

interface ISetNewPassword {
    type: typeof LoginUserActionType.SETNEW_PASSWORD,
    payload: ApiPayload,
    meta: {
        isApiCall: boolean
    },
    token: string
}


interface IFetchUserDataAction {
    type: typeof LoginUserActionType.FETCH_USERDATA,
    payload: ApiPayload,
    returnPayload: User,
    meta: {
        isApiCall: boolean
    },
}

interface ISettingsAction {
    type: typeof LoginUserActionType.FETCH_SETTINGS | typeof LoginUserActionType.UPDATE_SETTINGS,
    payload: ApiPayload,
    returnPayload: ProfileSettings,
    meta: {
        isApiCall: boolean
    },
}

interface IAddRole {
    type: typeof LoginUserActionType.ADD_ROLE_LOGIN_USER,
    payload: ApiPayload,
    returnPayload: LoginUser,
    meta: {
        isApiCall: boolean
    },
    token: string
}

interface IRemoveRole {
    type: typeof LoginUserActionType.REMOVE_ROLE_LOGIN_USER,
    payload: ApiPayload,
    returnPayload: LoginUser,
    meta: {
        isApiCall: boolean
    },
    token: string
}

interface IRegisterUser {
    type: typeof LoginUserActionType.REGISTER_USER,
    payload: ApiPayload,
    meta: {
        isApiCall: boolean
    },
    token: string
}

interface IDeleteAccount {
    type: typeof LoginUserActionType.DELETE_ACCOUNT,
    payload: ApiPayload,
    meta: {
        isApiCall: boolean
    },
    token: string
}

export type ILoginUserActions = ILoginUserAction & ILogOffUserAction & IFetchUserDataAction & IChangePassword & ISetNewPassword & IAddRole & IRemoveRole & IRegisterUser & IDeleteAccount & ISettingsAction;

function loginApiCall(loginData: LoginData): ApiPayload {
    return {
        url: 'api/auth',
        successAction: LoginUserActionType.LOGIN_USER_SUCCESS,
        errorAction: LoginUserActionType.LOGIN_ERROR,
        params: getLoginAPIParams(loginData),
        closeModal: true
    };
}

function getLoginAPIParams(loginData: LoginData): RequestInit {
    return {
        method: 'POST',
        mode: 'cors',
        cache: 'no-cache',
        headers: Api.getHeaders(),
        referrerPolicy: 'origin',
        body: JSON.stringify(loginData)
    }
};

export class LoginUserActions {
    public static loginUser(loginData: LoginData): LoginUserActions {
        return {
            type: LoginUserActionType.LOGIN_USER,
            payload: loginApiCall(loginData),
            meta: {
                isApiCall: true
            },
            token: ''
        }
    }

    public static logOffUser(): LoginUserActions {
        return {
            type: LoginUserActionType.LOGOFF_USER,
            token: ''
        }
    }

    public static changePassword(token: string, pwd: ChangePasswordData): LoginUserActions {
        return {
            type: LoginUserActionType.CHANGE_PASSWORD,
            payload: Api.postApiCall('api/auth/ChangePassword', token, LoginUserActionType.CHANGE_PASSWORD_SUCCESS, LoginUserActionType.API_ERROR, pwd, true),
            meta: {
                isApiCall: true
            },
        }
    }
    public static setNewPassword(pwd: ChangePasswordData): LoginUserActions {
        return {
            type: LoginUserActionType.SETNEW_PASSWORD,
            payload: Api.postApiCall('api/auth/SetNewPassword', "", LoginUserActionType.SETNEW_PASSWORD_SUCCESS, LoginUserActionType.API_ERROR, pwd, true),
            meta: {
                isApiCall: true
            },
        }
    }

    public static resetPassword(email: string): LoginUserActions {
        return {
            type: LoginUserActionType.RESET_PASSWORD,
            payload: Api.postApiCall('api/auth/ResetPassword', "", LoginUserActionType.RESET_PASSWORD_SUCCESS, LoginUserActionType.API_ERROR, email, true),
            meta: {
                isApiCall: true
            },
        }
    }

    public static addRole(token: string, roleData: ChangeRoleData): LoginUserActions {
        return {
            type: LoginUserActionType.ADD_ROLE_LOGIN_USER,
            payload: Api.postApiCall('api/UserData/addRole', token, LoginUserActionType.ADD_ROLE_LOGIN_USER_SUCCESS, LoginUserActionType.API_ERROR, roleData, true),
            meta: {
                isApiCall: true
            },
        }
    }

    public static removeRole(token: string, roleData: ChangeRoleData): LoginUserActions {
        return {
            type: LoginUserActionType.REMOVE_ROLE_LOGIN_USER,
            payload: Api.postApiCall('api/UserData/removeRole', token, LoginUserActionType.REMOVE_ROLE_LOGIN_USER_SUCCESS, LoginUserActionType.API_ERROR, roleData, true),
            meta: {
                isApiCall: true
            },
        }
    }

    public static fetchUserData(token: string): LoginUserActions {
        return {
            type: LoginUserActionType.FETCH_USERDATA,
            payload: Api.fetchApiCall('api/UserData', token, LoginUserActionType.FETCH_USERDATA_SUCCESS, LoginUserActionType.API_ERROR),
            returnPayload: User.empty(),
            meta: {
                isApiCall: true
            },
        }
    }
    public static fetchSettings(token: string): LoginUserActions {
        return {
            type: LoginUserActionType.FETCH_SETTINGS,
            payload: Api.fetchApiCall('api/UserData/Settings', token, LoginUserActionType.FETCH_SETTINGS_SUCCESS, LoginUserActionType.API_ERROR),
            meta: {
                isApiCall: true
            },
        }
    }

    public static updateSettings(token: string, settings: ProfileSettings): LoginUserActions {
        return {
            type: LoginUserActionType.UPDATE_SETTINGS,
            payload: Api.putApiCall('api/UserData/Settings', token, LoginUserActionType.UPDATE_SETTINGS_SUCCESS, LoginUserActionType.API_ERROR, settings),
            meta: {
                isApiCall: true
            },
        }
    }

    public static moveToRegister(): LoginUserActions {
        return push("/Account/register")
    }
    public static moveToHome(): LoginUserActions {
        return push("/")
    }

    public static registerUser(registerUser: RegisterUser): LoginUserActions {
        return {
            type: LoginUserActionType.REGISTER_USER,
            payload: Api.postApiCall('api/auth/Register', "", LoginUserActionType.REGISTER_USER_SUCCESS, LoginUserActionType.API_ERROR, registerUser, true),
            meta: {
                isApiCall: true
            },
        }
    }

    public static deleteAccount(token: string): LoginUserActions {
        return {
            type: LoginUserActionType.DELETE_ACCOUNT,
            payload: Api.postApiCall('api/UserData/DeleteAccount', token, LoginUserActionType.DELETE_ACCOUNT_SUCCESS, LoginUserActionType.API_ERROR, {}, true),
            meta: {
                isApiCall: true
            },
        }
    }

    // the router decoded the mail link once, re-encoding restores the form the backend decodes
    public static confirmDeleteAccount(email: string, token: string): LoginUserActions {
        return {
            type: LoginUserActionType.CONFIRM_DELETE_ACCOUNT,
            payload: Api.postApiCall('api/UserData/ConfirmDeleteAccount/' + encodeURIComponent(email) + '/' + encodeURIComponent(token), "", LoginUserActionType.CONFIRM_DELETE_ACCOUNT_SUCCESS, LoginUserActionType.API_ERROR, {}, true),
            meta: {
                isApiCall: true
            },
        }
    }
}

